/**
 * Plays pre-generated voice sample clips for preview.
 * Samples live in public/audio/voice-samples/{model}/{voiceId}.mp3
 */

import { capabilities } from '@/capabilities.ts';
import { sendToNative } from '@/bridge/embeddedBridge.ts';

let audioCtx: AudioContext | null = null;
let currentSource: AudioBufferSourceNode | null = null;
const cache = new Map<string, AudioBuffer>();

function getAudioContext(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  return audioCtx;
}

export async function playVoiceSample(
  model: 'kokoro' | 'elevenlabs',
  voiceId: string,
): Promise<void> {
  stopVoiceSample();

  if (capabilities.embeddedAudio) {
    // Route through Unity — build the file:// URL from BASE_URL
    const base = `${import.meta.env.BASE_URL}audio/voice-samples/${model}/${voiceId}`;
    // Send .mp3 — Unity/FMOD can't decode .m4a (AAC) on macOS
    sendToNative('playPreviewAudio', { url: `${base}.mp3` });
    return;
  }

  const ctx = getAudioContext();
  if (ctx.state === 'suspended') await ctx.resume();

  const key = `${model}/${voiceId}`;
  let buffer = cache.get(key);

  if (!buffer) {
    const base = `${import.meta.env.BASE_URL}audio/voice-samples/${model}/${voiceId}`;
    let response = await fetch(`${base}.mp3`);
    if (!response.ok) response = await fetch(`${base}.wav`);
    if (!response.ok) return; // sample not available
    const arrayBuffer = await response.arrayBuffer();
    buffer = await ctx.decodeAudioData(arrayBuffer);
    cache.set(key, buffer);
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.onended = () => {
    if (currentSource === source) currentSource = null;
  };
  currentSource = source;
  source.start();
}

export function stopVoiceSample(): void {
  if (capabilities.embeddedAudio) {
    sendToNative('stopPreviewAudio', {});
    return;
  }

  if (currentSource) {
    try { currentSource.stop(); } catch { /* already stopped */ }
    currentSource = null;
  }
}
